/**
 * NftAttachment — renders an `nft:<asset_id>` reference from a chat message.
 *
 * Resolution is cached per asset id for the lifetime of the app session, so a
 * conversation full of the same NFT costs one AtomicAssets request.
 *
 * Images from accounts the user has never written to are NOT loaded automatically:
 * fetching the media would both render whatever a stranger decided to send and
 * expose the user's IP to an IPFS gateway on someone else's command.
 */

import { useEffect, useState } from 'react'
import { loadNftsByIds, type NftAsset } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import { useGatewayImage } from '../hooks/useGatewayImage'

const cache = new Map<string, NftAsset | null>()
const inFlight = new Map<string, Promise<NftAsset | null>>()

async function resolveAsset(assetId: string): Promise<NftAsset | null> {
  if (cache.has(assetId)) return cache.get(assetId) ?? null
  const pending = inFlight.get(assetId)
  if (pending) return pending
  const task = loadNftsByIds([assetId])
    .then(list => {
      const asset = list.find(a => a.assetId === assetId) ?? null
      cache.set(assetId, asset)
      return asset
    })
    .catch(() => {
      cache.set(assetId, null)
      return null
    })
    .finally(() => inFlight.delete(assetId))
  inFlight.set(assetId, task)
  return task
}

export default function NftAttachment({
  assetId,
  trusted,
  senderAccount,
  onReveal,
}: {
  assetId: string
  /** Sender is someone the user has written to — safe to load media without asking. */
  trusted: boolean
  /**
   * Who sent the message. Attaching is only a reference, so anyone can point at
   * anyone else's NFT — when the sender is not the owner, say so.
   */
  senderAccount?: string
  /** Called when the user reveals media from an untrusted sender, so it can persist. */
  onReveal?: () => void
}) {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState(trusted)
  const [asset, setAsset] = useState<NftAsset | null | undefined>(cache.get(assetId))

  useEffect(() => {
    if (!revealed || asset !== undefined) return
    let cancelled = false
    void resolveAsset(assetId).then(a => {
      if (!cancelled) setAsset(a)
    })
    return () => {
      cancelled = true
    }
  }, [assetId, revealed, asset])

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={() => {
          onReveal?.()
          setRevealed(true)
        }}
        className="w-full text-left"
        style={{
          borderRadius: 10,
          border: '1px solid rgba(240,163,94,0.55)',
          background: 'rgba(240,163,94,0.12)',
          padding: '12px 14px',
          cursor: 'pointer',
        }}
      >
        <p className="text-xs flex items-center gap-1.5" style={{ color: '#f0a35e', fontWeight: 600 }}>
          <span aria-hidden>⚠️</span>
          {t('mechat.nftHidden')}
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#f6c390' }}>
          {t('mechat.nftReveal')} · #{assetId}
        </p>
      </button>
    )
  }

  if (asset === undefined) {
    return (
      <div
        style={{
          borderRadius: 10,
          background: 'rgba(255,255,255,0.04)',
          padding: '12px 14px',
        }}
      >
        <p className="text-xs" style={{ color: '#6b7280' }}>
          {t('common.loading')}
        </p>
      </div>
    )
  }

  if (asset === null) {
    return (
      <div
        style={{
          borderRadius: 10,
          border: '1px dashed rgba(255,255,255,0.14)',
          padding: '10px 12px',
        }}
      >
        <p className="text-xs" style={{ color: '#9ca3af' }}>
          {t('mechat.nftNotFound')} #{assetId}
        </p>
      </div>
    )
  }

  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', background: 'rgba(0,0,0,0.35)' }}>
      <NftAttachmentMedia mediaUrl={asset.mediaUrl} mediaType={asset.mediaType} />
      <div className="px-3 py-2">
        <p className="text-xs text-white font-medium truncate">{asset.name}</p>
        {senderAccount && asset.owner && asset.owner !== senderAccount && (
          <p className="text-[10px]" style={{ color: '#f0a35e' }}>
            {t('mechat.nftNotOwned')}
          </p>
        )}
        <p className="text-[10px] truncate" style={{ color: '#6b7280' }}>
          {asset.collection ? `${asset.collection} · ` : ''}#{asset.assetId}
        </p>
      </div>
    </div>
  )
}

/** Media for one revealed attachment — isolated so gateway fallback runs per message. */
function NftAttachmentMedia({
  mediaUrl,
  mediaType,
}: {
  mediaUrl: string | null
  mediaType: NftAsset['mediaType']
}) {
  const { t } = useTranslation()
  const { src, failed, onError, imgProps } = useGatewayImage(mediaUrl)

  if (!mediaUrl || failed || !src) {
    return (
      <div className="px-3 py-4">
        <p className="text-xs" style={{ color: '#9ca3af' }}>
          {t('mechat.nftNoMedia')}
        </p>
      </div>
    )
  }

  if (mediaType === 'video') {
    return (
      <video
        key={src}
        src={src}
        controls
        playsInline
        style={{ display: 'block', width: '100%', maxHeight: 260 }}
        {...imgProps}
      />
    )
  }

  return (
    <img
      key={src}
      src={src}
      alt=""
      loading="lazy"
      onError={onError}
      style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }}
      {...imgProps}
    />
  )
}

